import { SidePanelLayer, SidePanelLayerOrGroup } from "@nova/constants"
import { UsageModel } from "@nova/db-table-usage"
import { useEffect, useMemo, useState } from "react"
import { cachedUsageByEntityId } from "./constants"
import { throttleAsync } from "nodash"

export async function getUsage({
    entity,
    completedUploadCount,
    abortController,
}: {
    entity: SidePanelLayerOrGroup | undefined
    completedUploadCount?: number
    abortController?: AbortController
}) {
    if (
        !entity ||
        !("images" in entity) ||
        ("images" in entity && entity.images?.length === 0 && !("job" in entity))
    ) {
        return { usageAmount: 0 } as UsageModel
    }
    if (
        cachedUsageByEntityId[entity.id] &&
        (cachedUsageByEntityId[entity.id].fileCount === completedUploadCount ||
            completedUploadCount === undefined)
    ) {
        return cachedUsageByEntityId[entity.id].usage
    }
    try {
        const response = await fetch(`/api/usage/entity/${entity.id}`, {
            signal: abortController?.signal,
        })
        const usage = (await response.json()) as UsageModel

        cachedUsageByEntityId[entity.id] = { fileCount: completedUploadCount || 0, usage }
        return usage
    } catch (error) {
        if (error !== "cancel") {
            console.error(error)
            return { usageAmount: 0 } as UsageModel
        }
    }
}

export const useInspectorPanelUsage = (
    entity: SidePanelLayer | undefined,
    completedUploadCount: number
) => {
    const [usage, setUsage] = useState<UsageModel | undefined>(
        entity ? cachedUsageByEntityId[entity.id]?.usage : undefined
    )

    const fetchUsage = useMemo(
        () =>
            throttleAsync(
                ({ entity, completedUploadCount, abortController }) =>
                    getUsage({ entity, completedUploadCount, abortController }),
                10000,
                1
            ),
        []
    )

    // Fetch usage then cache it.
    useEffect(() => {
        if (!entity?.id) {
            setUsage(undefined)
            return
        } else {
            setUsage(cachedUsageByEntityId[entity.id]?.usage)
        }

        const abortController = new AbortController()

        const fetchAndSetUsage = async () => {
            try {
                const usage = await fetchUsage({ entity, completedUploadCount, abortController })
                setUsage(usage)
            } catch {}
        }
        fetchAndSetUsage()

        return () => {
            abortController.abort("cancel")
        }
    }, [completedUploadCount, entity, fetchUsage])

    return usage
}

export function useNoUsage() {
    // This is a stub that allows for usage checks for entities including non-layers (ie. groups)
    return { usageAmount: 0 }
}
